import React, { useEffect, useState, useRef } from 'react';
import { useNotify, useTranslate, usePermissions } from 'react-admin';
import {
  Alert, Box, Button, Card, CircularProgress, Divider, FormControlLabel, FormGroup, Grid, MenuItem, Stack, Switch,
  Tab, Tabs, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useTheme,
} from '@mui/material';
import {
  HealthAndSafetyOutlined as HealthIcon,
  PhotoCameraOutlined as ImageIcon,
  RefreshRounded as ScanAgainIcon,
  RestartAltRounded as NewDocumentIcon,
  ScienceOutlined as TestCaseIcon,
  SendRounded as SendIcon,
} from '@mui/icons-material';
import { BlockHeading, DataTable, FormSection, PageTitle, SectionHeader } from '@/components/shell';
import { httpClient } from '@/utils/httpClient';
import { USER_ROLES } from '@/config/constants';
import { FaceTestPanel } from './FaceTestPanel';
import { combineImages, compressImage, stringifyResponse } from './testUtils';
import { CodeBlock, Disclosure } from '@/components/shell';
import { FilePreview, ImageDropzone, PDF_PREVIEW_PREFIX } from './ImageDropzone';
import { ResultSummary, extractGeoData } from './ResultSummary';
import {
  DebugView, EmptyNote, FaceView, GroupedFieldsView, KeyValueTable, MrzView, NormView, VizView, groupBySource,
  valueEntries,
} from './OcrResultViews';
import { TestPageHelp } from './TestPageHelp';
import { CreateTestCaseDialog } from './CreateTestCaseDialog';

export type UploadMode = 'single' | 'dual-combined' | 'dual-separate' | 'midni';
type TransportMode = 'base64' | 's3' | 'file';
type Side = 'front' | 'back';
type DataMode = 'mapping' | 'raw' | 'compare';

const UPLOAD_MODES: Array<{ mode: UploadMode; labelKey: string }> = [
  { mode: 'single', labelKey: 'test.single_side' },
  { mode: 'dual-combined', labelKey: 'test.dual_combined' },
  { mode: 'dual-separate', labelKey: 'test.dual_separate' },
  { mode: 'midni', labelKey: 'test.midni' },
];

const TRANSPORT_MODES: Array<{ mode: TransportMode; labelKey: string }> = [
  { mode: 'base64', labelKey: 'screens.testPage.transportBase64' },
  { mode: 's3', labelKey: 'screens.testPage.transportS3' },
  { mode: 'file', labelKey: 'screens.testPage.transportFile' },
];

// Resizable image panel — width as a % of the row, persisted across sessions.
// Only applies on desktop; mobile stacks vertically (see render below).
const IMAGE_PANEL_STORAGE_KEY = 'kyc-test-image-panel-width-pct';
const IMAGE_PANEL_MIN_PCT = 20;
const IMAGE_PANEL_MAX_PCT = 80;

/** A tab of the result: its label (with the count), the help line under the strip, and whether it has anything. */
type TabDef = { id: string; label: string; help?: string; disabled?: boolean };

export const PhotoUploadPage = () => {
  const notify = useNotify();
  const t = useTranslate();
  const theme = useTheme();
  const { permissions } = usePermissions<string>();
  const isAdmin = permissions === USER_ROLES.ADMIN;

  // Top-level section: 'ocr' or 'face'
  const [section, setSection] = useState<'ocr' | 'face'>('ocr');
  const [helpOpen, setHelpOpen] = useState(false);

  // Settings
  const [accounts, setAccounts] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [uploadMode, setUploadMode] = useState<UploadMode>('single');
  const [transportMode, setTransportMode] = useState<TransportMode>('base64');
  const [faceVectorEnabled, setFaceVectorEnabled] = useState(false);
  const [returnFace, setReturnFace] = useState(false);
  const [returnSignature, setReturnSignature] = useState(false);
  const [mappingEnabled, setMappingEnabled] = useState(true);
  const [debugEnabled, setDebugEnabled] = useState(false);
  const [includeRaw, setIncludeRaw] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const canDebug = accounts.find(a => a.accountResponse?.accountName === selectedAccount)?.accountResponse?.allowDebugOutput ?? false;

  // Images (base64 for the request, a URL for the preview)
  const [frontImage, setFrontImage] = useState<string | null>(null);
  const [backImage, setBackImage] = useState<string | null>(null);
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [backPreview, setBackPreview] = useState<string | null>(null);

  // State
  const [processing, setProcessing] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [rawData, setRawData] = useState<any>(null);
  const [mappedData, setMappedData] = useState<any>(null);
  const [compareData, setCompareData] = useState<{ mapped: { data: any; time: number }; raw: { data: any; time: number } } | null>(null);
  const [processingStep, setProcessingStep] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const [healthResult, setHealthResult] = useState<any>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [roundTripMs, setRoundTripMs] = useState(0);
  const [showImagePanel, setShowImagePanel] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [showTestCaseDialog, setShowTestCaseDialog] = useState(false);

  const [imagePanelWidthPct, setImagePanelWidthPct] = useState<number>(() => {
    const stored = Number(localStorage.getItem(IMAGE_PANEL_STORAGE_KEY));
    return Number.isFinite(stored) && stored >= IMAGE_PANEL_MIN_PCT && stored <= IMAGE_PANEL_MAX_PCT ? stored : 40;
  });
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  useEffect(() => {
    const updateFromClientX = (clientX: number) => {
      const el = splitContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const tabsWidthPx = Math.max(0, clientX - rect.left);
      const imagePct = 100 - (tabsWidthPx / rect.width) * 100;
      const clamped = Math.max(IMAGE_PANEL_MIN_PCT, Math.min(IMAGE_PANEL_MAX_PCT, imagePct));
      setImagePanelWidthPct(clamped);
    };
    const onMouseMove = (e: MouseEvent) => { if (draggingRef.current) updateFromClientX(e.clientX); };
    const onTouchMove = (e: TouchEvent) => { if (draggingRef.current && e.touches[0]) updateFromClientX(e.touches[0].clientX); };
    const stop = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', stop);
    window.addEventListener('touchcancel', stop);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', stop);
      window.removeEventListener('touchcancel', stop);
    };
  }, []);
  useEffect(() => {
    localStorage.setItem(IMAGE_PANEL_STORAGE_KEY, String(imagePanelWidthPct));
  }, [imagePanelWidthPct]);
  const startSplitterDrag = () => {
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const p = new URLSearchParams({ draw: '1', length: '1000', start: '0', search: '', column: '', orderBy: 'asc' });
    httpClient.get<any>(`/admin/account/GetAccounts?${p}`).then(r => {
      setAccounts((r.data.data || []).filter((a: any) => a.accountResponse?.accountStatus === 'Active'));
    });
  }, []);

  const isValidFile = (file: File) => file.type.startsWith('image/') || file.type === 'application/pdf';

  const storeFile = async (file: File, side: Side) => {
    if (!isValidFile(file)) { notify('screens.testPage.invalidFile', { type: 'warning' }); return; }
    const b64 = await compressImage(file);
    const blobUrl = URL.createObjectURL(file);
    const preview = file.type === 'application/pdf' ? `${PDF_PREVIEW_PREFIX}${blobUrl}` : blobUrl;
    if (side === 'front') { setFrontImage(b64); setFrontPreview(preview); }
    else { setBackImage(b64); setBackPreview(preview); }
  };

  const handleCameraCapture = (b64: string, side: Side) => {
    const preview = `data:image/jpeg;base64,${b64}`;
    if (side === 'front') { setFrontImage(b64); setFrontPreview(preview); }
    else { setBackImage(b64); setBackPreview(preview); }
  };

  const removeImage = (side: Side) => {
    if (side === 'front') { setFrontImage(null); setFrontPreview(null); }
    else { setBackImage(null); setBackPreview(null); }
  };

  /** Send a single request with given params */
  const sendRequest = async (params: Record<string, any>) => {
    if (transportMode === 'file') {
      const formData = new FormData();
      Object.entries(params).forEach(([k, v]) => formData.append(k, String(v)));
      if (uploadMode === 'dual-combined' && frontImage && backImage) {
        const combined = await combineImages(frontImage, backImage);
        const blob = await fetch(`data:image/jpeg;base64,${combined}`).then(r => r.blob());
        formData.append('image', blob, 'combined.jpg');
      } else {
        if (frontImage) {
          const blob = await fetch(`data:image/jpeg;base64,${frontImage}`).then(r => r.blob());
          formData.append('image', blob, 'image.jpg');
        }
        if (uploadMode === 'dual-separate' && backImage) {
          const blob = await fetch(`data:image/jpeg;base64,${backImage}`).then(r => r.blob());
          formData.append('image2', blob, 'image2.jpg');
        }
      }
      return httpClient.post<any>('/admin/test/process', formData);
    } else {
      let image = frontImage;
      let image2: string | undefined;
      if (uploadMode === 'dual-combined' && backImage) {
        image = await combineImages(frontImage!, backImage);
      } else if (uploadMode === 'dual-separate' && backImage) {
        image2 = backImage;
      }
      return httpClient.post<any>('/admin/test/process', { ...params, image, image2 });
    }
  };

  const processDocument = async () => {
    if (!frontImage || !selectedAccount) { notify(t('test.select_account_image'), { type: 'warning' }); return; }
    setProcessing(true); setProcessingStep(''); setErrorMsg(''); setErrorDetail(''); setHasResults(false);
    setRawData(null); setMappedData(null); setCompareData(null);

    const baseParams = {
      accountName: selectedAccount,
      captureMode: uploadMode,
      transportMode,
      returnFace,
      returnSignature,
      faceVector: faceVectorEnabled,
      detectOrientation: true,
    };

    try {
      if (compareMode) {
        // Two parallel calls — one mapped, one raw — so each side shows the exact
        // response an integrator would receive for that mode.
        setProcessingStep(t('test.processing'));
        const t0 = performance.now();
        const [rMapped, rRaw] = await Promise.all([
          sendRequest({ ...baseParams, mapping: true, debug: false, includeRaw: false }),
          sendRequest({ ...baseParams, mapping: false, debug: false, includeRaw: false }),
        ]);
        const roundTrip = Math.round(performance.now() - t0);

        setCompareData({
          mapped: { data: rMapped.data, time: roundTrip },
          raw: { data: rRaw.data, time: roundTrip },
        });
        setHasResults(true);
        setTabValue(0);
      } else {
        const t0 = performance.now();
        const r = await sendRequest({
          ...baseParams,
          mapping: mappingEnabled,
          debug: debugEnabled,
          includeRaw,
        });
        setRoundTripMs(Math.round(performance.now() - t0));
        if (mappingEnabled) setMappedData(r.data);
        else setRawData(r.data);
        setHasResults(true);
        setTabValue(0);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || t('screens.testPage.unknownError'));
      if (err?.data) setErrorDetail(JSON.stringify(err.data, null, 2));
    } finally { setProcessing(false); setProcessingStep(''); }
  };

  const checkHealth = async () => {
    if (!selectedAccount) { notify(t('test.select_account'), { type: 'warning' }); return; }
    setHealthLoading(true); setHealthResult(null);
    try {
      const r = await httpClient.get<any>(`/admin/test/health?accountName=${encodeURIComponent(selectedAccount)}`);
      setHealthResult(r.data);
    } catch { notify('screens.testPage.healthFailed', { type: 'error' }); }
    finally { setHealthLoading(false); }
  };

  const resetAll = () => {
    setFrontImage(null); setBackImage(null);
    setFrontPreview(null); setBackPreview(null);
    setHasResults(false); setRawData(null); setMappedData(null); setCompareData(null);
    setErrorMsg(''); setErrorDetail('');
  };

  // Data mode: mapping / raw / compare (mutually exclusive)
  const currentDataMode: DataMode = compareMode ? 'compare' : includeRaw ? 'raw' : 'mapping';
  const setDataMode = (mode: DataMode) => {
    setCompareMode(mode === 'compare');
    setMappingEnabled(mode === 'mapping');
    setIncludeRaw(mode === 'raw');
    if (mode !== 'mapping') setDebugEnabled(false);
  };

  const header = (
    <SectionHeader
      left={<PageTitle title={t('menu.photo_upload')} subtitle={t('screens.testPage.subtitle')} />}
      help={section === 'ocr' ? { label: t('screens.testPage.helpLabel'), onClick: () => setHelpOpen(true) } : undefined}
    />
  );

  /* The OCR / face switch: the two tools of the page. */
  const sectionTabs = (
    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 2.5 }}>
      <Tabs value={section} onChange={(_, value: 'ocr' | 'face') => setSection(value)} aria-label={t('screens.testPage.tool')}>
        <Tab value="ocr" label={t('test.section_ocr')} />
        <Tab value="face" label={t('test.section_face')} />
      </Tabs>
    </Box>
  );

  const page = (body: React.ReactNode) => (
    <Box sx={{ pb: 3 }}>
      {header}
      {sectionTabs}
      {body}
      <TestPageHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );

  if (section === 'face') return page(<FaceTestPanel />);

  /* ══════════════════════════════════════ */
  /* ██  RENDER — INPUT STATE             ██ */
  /* ══════════════════════════════════════ */

  if (!hasResults) {
    const needsDualImage = uploadMode !== 'single';
    const options: Array<{ label: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }> = [
      { label: t('test.face'), checked: returnFace, onChange: setReturnFace },
      { label: t('test.signature'), checked: returnSignature, onChange: setReturnSignature },
      { label: t('test.face_vector'), checked: faceVectorEnabled, onChange: setFaceVectorEnabled },
      {
        label: t('screens.testPage.debug'),
        checked: debugEnabled,
        disabled: currentDataMode !== 'mapping' || !canDebug,
        onChange: setDebugEnabled,
      },
    ];

    const health = healthResult && (() => {
      const primary = healthResult.ocr?.primary ?? healthResult.primaryServer;
      const rawSecondary = healthResult.ocr?.secondary ?? healthResult.secondaryServer;
      const secondary = rawSecondary?.name === primary?.name ? null : rawSecondary;
      const primaryOk = primary?.healthy ?? primary?.status === 'Online';
      const secondaryOk = secondary?.healthy ?? secondary?.status === 'Online';
      const line = (role: string, server: any, ok: boolean) => t('screens.testPage.healthLine', {
        role: t(`screens.testPage.${role}`),
        name: server.name,
        state: t(ok ? 'screens.testPage.healthy' : 'screens.testPage.down'),
      });
      return (
        <Alert severity={primaryOk || secondaryOk ? 'success' : 'error'}>
          {primary && <Box>{line('primary', primary, primaryOk)}</Box>}
          {secondary && <Box>{line('secondary', secondary, secondaryOk)}</Box>}
        </Alert>
      );
    })();

    const dropzone = (side: Side) => (
      <ImageDropzone
        label={t(side === 'front' ? 'test.front_image' : 'test.back_image')}
        preview={side === 'front' ? frontPreview : backPreview}
        accept="image/*,.pdf"
        onFile={(file) => storeFile(file, side)}
        onRemove={() => removeImage(side)}
        onCapture={(b64) => handleCameraCapture(b64, side)}
        facingMode="environment"
        idealWidth={1920}
        previewMaxHeight={240}
      />
    );

    return page(
      // A Box around the Grid: its negative gutter needs the margins a Stack would strip.
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Stack spacing={3}>
                <FormSection title={t('screens.testPage.accountSection')}>
                  <TextField
                    select
                    fullWidth
                    label={t('test.account')}
                    value={selectedAccount}
                    onChange={(e) => {
                      const name = e.target.value;
                      setSelectedAccount(name);
                      const acct = accounts.find((a: any) => a.accountResponse?.accountName === name);
                      if (!acct?.accountResponse?.allowDebugOutput) {
                        setDebugEnabled(false);
                        setDataMode('mapping');
                      }
                    }}
                  >
                    {accounts.map((a: any) => (
                      <MenuItem key={a.accountResponse?.accountName} value={a.accountResponse?.accountName}>
                        {a.accountResponse?.accountName}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormSection>

                <FormSection title={t('screens.testPage.howToSend')}>
                  <TextField
                    select
                    fullWidth
                    label={t('screens.testPage.captureMode')}
                    value={uploadMode}
                    onChange={(e) => setUploadMode(e.target.value as UploadMode)}
                  >
                    {UPLOAD_MODES.map((m) => <MenuItem key={m.mode} value={m.mode}>{t(m.labelKey)}</MenuItem>)}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    label={t('screens.testPage.transport')}
                    value={transportMode}
                    onChange={(e) => setTransportMode(e.target.value as TransportMode)}
                  >
                    {TRANSPORT_MODES.map((m) => <MenuItem key={m.mode} value={m.mode}>{t(m.labelKey)}</MenuItem>)}
                  </TextField>
                </FormSection>

                {/* Options — hidden in MiDNI mode */}
                {uploadMode !== 'midni' && (
                  <FormSection title={t('screens.testPage.whatToReturn')}>
                    <TextField
                      select
                      fullWidth
                      label={t('screens.testPage.dataMode')}
                      value={currentDataMode}
                      onChange={(e) => setDataMode(e.target.value as DataMode)}
                      helperText={canDebug ? undefined : t('screens.testPage.dataModeHint')}
                    >
                      <MenuItem value="mapping">{t('test.mapping')}</MenuItem>
                      <MenuItem value="raw" disabled={!canDebug}>{t('screens.testPage.raw')}</MenuItem>
                      <MenuItem value="compare" disabled={!canDebug}>{t('screens.testPage.compare')}</MenuItem>
                    </TextField>
                    <FormGroup>
                      {options.map((opt) => (
                        <FormControlLabel
                          key={opt.label}
                          label={opt.label}
                          disabled={opt.disabled}
                          control={<Switch checked={opt.checked} onChange={(e) => opt.onChange(e.target.checked)} />}
                        />
                      ))}
                    </FormGroup>
                  </FormSection>
                )}

                <Stack spacing={1.25}>
                  <Button
                    variant="contained"
                    onClick={processDocument}
                    fullWidth
                    disabled={processing || !frontImage || !selectedAccount}
                    startIcon={processing ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                  >
                    {processing ? (processingStep || t('test.processing')) : t('test.process')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={checkHealth}
                    fullWidth
                    disabled={healthLoading || !selectedAccount}
                    startIcon={healthLoading ? <CircularProgress size={16} color="inherit" /> : <HealthIcon />}
                  >
                    {t('screens.testPage.healthCheck')}
                  </Button>
                </Stack>

                {health}

                {errorMsg && (
                  <Alert severity="error">
                    {errorMsg}
                    {errorDetail && (
                      <Disclosure label={t('test.details')}>
                        <CodeBlock text={errorDetail} />
                      </Disclosure>
                    )}
                  </Alert>
                )}
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Stack spacing={2.5} divider={<Divider />}>
                {dropzone('front')}
                {needsDualImage && dropzone('back')}
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Box>,
    );
  }

  /* ══════════════════════════════════════ */
  /* ██  RENDER — RESULTS STATE           ██ */
  /* ══════════════════════════════════════ */

  const resultActions = (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      <Button variant="outlined" startIcon={<ScanAgainIcon />} onClick={() => setHasResults(false)}>
        {t('test.scan_again')}
      </Button>
      <Button variant="outlined" startIcon={<NewDocumentIcon />} onClick={resetAll}>
        {t('test.new_document')}
      </Button>
      {isAdmin && (
        <Button variant="outlined" startIcon={<TestCaseIcon />} onClick={() => setShowTestCaseDialog(true)}>
          {t('test.create_test_case')}
        </Button>
      )}
    </Stack>
  );

  /** The tab strip, with the image panel's toggle at its end and the active tab's help under it. */
  const tabStrip = (tabs: TabDef[], activeHelp?: string) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Tabs
          value={Math.min(tabValue, tabs.length - 1)}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label={t('screens.testPage.resultViews')}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {tabs.map((tab) => <Tab key={tab.id} disabled={tab.disabled} label={tab.label} />)}
        </Tabs>
        {frontPreview && (
          <Button
            variant="text"
            startIcon={<ImageIcon />}
            onClick={() => setShowImagePanel((p) => !p)}
            aria-pressed={showImagePanel}
            sx={{ flexShrink: 0 }}
          >
            {showImagePanel ? t('screens.testPage.hideImage') : t('screens.testPage.showImage')}
          </Button>
        )}
      </Box>
      {activeHelp && (
        <Typography variant="body2" sx={{ color: theme.custom.text.tertiary, pt: 1.5 }}>{activeHelp}</Typography>
      )}
    </Box>
  );

  /** The tab content beside the document's images (resizable on desktop, stacked on a phone). */
  const withImagePanel = (content: React.ReactNode) => {
    const panelShown = showImagePanel && Boolean(frontPreview);
    return (
      <Box ref={splitContainerRef} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ minWidth: 0, width: { xs: '100%', md: panelShown ? `${100 - imagePanelWidthPct}%` : '100%' }, flexShrink: 0, pr: { md: panelShown ? 1.5 : 0 } }}>
          {content}
        </Box>

        {/* Splitter — desktop only, drag to resize the image panel */}
        {panelShown && (
          <Box
            role="separator"
            aria-orientation="vertical"
            aria-label={t('screens.testPage.resizeImagePanel')}
            onMouseDown={startSplitterDrag}
            onTouchStart={startSplitterDrag}
            sx={{
              display: { xs: 'none', md: 'flex' },
              width: 6, flexShrink: 0,
              cursor: 'col-resize',
              position: 'relative',
              '&::before': {
                content: '""', position: 'absolute', top: 0, bottom: 0, left: '50%',
                transform: 'translateX(-50%)', width: 1, bgcolor: 'divider',
                transition: 'background-color 0.15s, width 0.15s',
              },
              '&:hover::before, &:active::before': { width: 3, bgcolor: 'primary.main' },
            }}
          />
        )}

        {panelShown && (
          <Box sx={{ width: { xs: '100%', md: `${imagePanelWidthPct}%` }, flexShrink: 0, pl: { md: 1.5 }, minWidth: 0 }}>
            <Card sx={{ p: 1.5 }}>
              <Stack spacing={1.5}>
                <FilePreview preview={frontPreview!} alt={t('test.front_image')} />
                {backPreview && <FilePreview preview={backPreview} alt={t('test.back_image')} />}
              </Stack>
            </Card>
          </Box>
        )}
      </Box>
    );
  };

  const results = (summary: React.ReactNode, strip: React.ReactNode, content: React.ReactNode) => (
    <Stack spacing={2.5}>
      <Box component="section" aria-labelledby="test-result">
        <BlockHeading id="test-result" title={t('screens.testPage.result')} action={resultActions} />
        {summary}
      </Box>
      {strip}
      {withImagePanel(content)}
    </Stack>
  );

  const createCaseDialog = (
    <CreateTestCaseDialog
      open={showTestCaseDialog}
      onClose={() => setShowTestCaseDialog(false)}
      uploadMode={uploadMode}
      frontImage={frontImage}
      backImage={backImage}
      rawData={rawData}
      mappedData={mappedData}
      compareMode={compareMode}
      compareData={compareData}
    />
  );

  /* ── Compare mode results ── */
  if (compareMode && compareData) {
    const cmpMapped = compareData.mapped.data;
    const cmpRaw = compareData.raw.data;
    const cmpMappedEntries = valueEntries(cmpMapped);
    const cmpRawKV: Record<string, string> = cmpRaw?.keyValues || {};

    // Find best raw match for a mapped field name
    const findRawValue = (mappedKey: string): string => {
      const lower = mappedKey.toLowerCase();
      const exact = Object.entries(cmpRawKV).find(([k]) => k.toLowerCase().endsWith(lower));
      if (exact) return exact[1];
      const partial = Object.entries(cmpRawKV).find(([k]) => k.toLowerCase().includes(lower));
      return partial ? partial[1] : '';
    };

    const compareTabs: TabDef[] = [
      { id: 'summary', label: t('screens.testPage.compareSummary') },
      { id: 'detail-mapped', label: t('screens.testPage.compareDetailMapped') },
      { id: 'detail-raw', label: t('screens.testPage.compareDetailRaw') },
      { id: 'json', label: t('screens.testPage.json') },
    ];
    const cmpActiveTab = compareTabs[tabValue]?.id || 'summary';

    return page(
      <>
        {results(
          <ResultSummary
            face={cmpRaw?.face || cmpMapped?.Face || ''}
            name={cmpMapped?.FullName || cmpMapped?.GivenNames || cmpRawKV['NORM_FRONT_ENG_FullName'] || ''}
            details={[
              cmpMapped?.Description || cmpRaw?.description || '',
              cmpMapped?.DocumentNumber || cmpRawKV['NORM_FRONT_ENG_DocumentNumber'] || '',
            ].filter(Boolean).join(' · ')}
            timings={{
              ocr: cmpRaw?.responseTime || cmpMapped?.ResponseTime || 0,
              gateway: cmpRaw?.gatewayTime || cmpMapped?.GatewayTime || 0,
              total: compareData.mapped.time,
            }}
            geo={extractGeoData(cmpMapped?.GeoData || null, cmpMapped || null, cmpRawKV)}
            docTypeCandidates={[cmpMapped?.Description, cmpRaw?.documentInfo?.documentType, cmpMapped?.DocumentIdentification]}
            nationalityCandidates={[cmpMapped?.GeoData?.NationalityAlpha3, cmpMapped?.GeoData?.NationalityAlpha2, cmpMapped?.Nationality, cmpRaw?.documentInfo?.nationality]}
          />,
          tabStrip(compareTabs),
          <>
            {cmpActiveTab === 'summary' && (
              <DataTable label={t('screens.testPage.compareSummary')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('test.field')}</TableCell>
                    <TableCell>{t('test.mapping')}</TableCell>
                    <TableCell>{t('screens.testPage.raw')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cmpMappedEntries.map(([k, v], i) => {
                    const rawVal = findRawValue(k);
                    const differs = Boolean(rawVal) && rawVal !== v;
                    return (
                      <TableRow key={i}>
                        <TableCell>{k}</TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{v}</TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere', color: differs ? 'warning.dark' : rawVal ? 'text.primary' : 'text.secondary', fontWeight: differs ? 500 : undefined }}>
                          {rawVal || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </DataTable>
            )}
            {cmpActiveTab === 'detail-mapped' && (cmpMappedEntries.length > 0
              ? <KeyValueTable label={t('screens.testPage.compareDetailMapped')} entries={cmpMappedEntries} />
              : <EmptyNote>{t('test.no_mapping_data')}</EmptyNote>)}
            {cmpActiveTab === 'detail-raw' && <GroupedFieldsView grouped={groupBySource(cmpRawKV, cmpRaw?.keyScores || {})} />}
            {cmpActiveTab === 'json' && (
              <CodeBlock text={stringifyResponse(compareData.mapped.data)} copyText={JSON.stringify(compareData, null, 2)} />
            )}
          </>,
        )}
        {createCaseDialog}
      </>,
    );
  }

  /* ── Single-mode results ── */
  const keyValues: Record<string, string> = rawData?.keyValues || {};
  const keyScores: Record<string, number> = rawData?.keyScores || {};
  const docInfo = rawData?.documentInfo;

  const mappedEntries = valueEntries(mappedData);
  const debugEntries = mappedData?.MappingDebug || [];

  const rawSdk = rawData?.raw;
  const mrzPages = (rawSdk?.mrz || rawData?.mrz || []).filter((p: any) => p?.fields?.length > 0);
  const vizPages = (rawSdk?.viz || rawData?.viz || []).filter((p: any) => p?.fields?.length > 0);
  const normPages = (rawSdk?.norm || rawData?.norm || []).filter((p: any) => p?.fields?.length > 0);

  const faceB64 = rawData?.face || mappedData?.Face || '';
  const signatureB64 = rawData?.signature || rawSdk?.signature || mappedData?.Signature || '';
  const faceMatch = rawData?.faceMatchAccuracy || mappedData?.FaceMatchAccuracy;
  const faceVectorData = rawData?.faceVector || rawData?.FaceVector || mappedData?.FaceVector;

  const description = rawData?.description || mappedData?.Description || docInfo?.documentType || mrzPages[0]?.description || '';
  const heroName = mappedData?.FullName || mappedData?.GivenNames ||
    keyValues['NORM_FRONT_ENG_FullName'] || keyValues['NORM_FRONT_MRZ_FullName'] ||
    keyValues['VIZ_GivenNames'] || keyValues['MRZ_GivenNames'] || '';
  const heroDocNum = mappedData?.DocumentNumber ||
    keyValues['NORM_FRONT_ENG_DocumentNumber'] || keyValues['VIZ_DocumentNumber'] || keyValues['MRZ_DocumentNumber'] || '';

  const pageFieldCount = (pages: any[]) => pages.reduce((s: number, p: any) => s + (p.fields?.length || 0), 0);
  const tabCounts = {
    fields: Object.keys(keyValues).length,
    mapped: mappedEntries.length,
    mrz: pageFieldCount(mrzPages),
    viz: pageFieldCount(vizPages),
    norm: pageFieldCount(normPages),
    debug: debugEntries.length,
    face: (faceB64 ? 1 : 0) + (signatureB64 ? 1 : 0) + (faceVectorData ? 1 : 0),
  };
  const counted = (label: string, count: number) => t('screens.testPage.tabCount', { label, count });

  // Build visible tabs dynamically based on active options
  const visibleTabs: TabDef[] = [];
  if (!mappingEnabled) {
    // Raw mode: show raw field tabs
    visibleTabs.push({ id: 'fields', label: counted(t('test.all_fields'), tabCounts.fields), help: t('test.tab_help.all_fields'), disabled: !tabCounts.fields });
    visibleTabs.push({ id: 'mrz', label: counted('MRZ', tabCounts.mrz), help: t('test.tab_help.mrz'), disabled: !tabCounts.mrz });
    visibleTabs.push({ id: 'viz', label: counted('VIZ', tabCounts.viz), help: t('test.tab_help.viz'), disabled: !tabCounts.viz });
    visibleTabs.push({ id: 'norm', label: counted('NORM', tabCounts.norm), help: t('test.tab_help.norm'), disabled: !tabCounts.norm });
  } else {
    // Mapped mode: show mapped + debug tabs
    visibleTabs.push({ id: 'mapped', label: counted(t('test.mapped_data'), tabCounts.mapped), help: t('test.tab_help.mapped_data'), disabled: !tabCounts.mapped });
    if (debugEnabled) {
      visibleTabs.push({ id: 'debug', label: counted(t('test.mapping_debug'), tabCounts.debug), help: t('test.tab_help.debug'), disabled: !tabCounts.debug });
    }
  }
  // Always show face (if requested) and JSON
  if (returnFace || returnSignature || faceVectorEnabled) {
    visibleTabs.push({ id: 'face', label: counted(t('test.face_signature'), tabCounts.face), help: t('test.tab_help.face'), disabled: !tabCounts.face });
  }
  visibleTabs.push({ id: 'json', label: t('screens.testPage.json'), help: t('test.tab_help.json') });

  const activeTab = visibleTabs[tabValue] || visibleTabs[0];
  // Only one of rawData / mappedData is populated per call (depends on
  // whether mapping was enabled). Render the actual gateway response
  // verbatim, with no `{ raw, mapped }` envelope.
  const responseJson = mappedData ?? rawData;

  return page(
    <>
      {results(
        <ResultSummary
          face={faceB64}
          name={heroName}
          details={[description, heroDocNum, docInfo?.nationality].filter(Boolean).join(' · ')}
          timings={{
            ocr: rawData?.responseTime || mappedData?.ResponseTime || rawSdk?.duration || rawSdk?.responseTime || 0,
            gateway: rawData?.gatewayTime || mappedData?.GatewayTime || rawSdk?.gatewayTime || 0,
            total: roundTripMs,
          }}
          fieldCount={Object.keys(keyValues).length}
          geo={extractGeoData(mappedData?.GeoData || null, mappedData || null, keyValues)}
          docTypeCandidates={[mappedData?.Description, docInfo?.documentType, mappedData?.DocumentIdentification]}
          nationalityCandidates={[mappedData?.GeoData?.NationalityAlpha3, mappedData?.GeoData?.NationalityAlpha2, mappedData?.Nationality, docInfo?.nationality]}
        />,
        tabStrip(visibleTabs, activeTab?.help),
        <>
          {activeTab?.id === 'fields' && <GroupedFieldsView grouped={groupBySource(keyValues, keyScores)} />}
          {activeTab?.id === 'mapped' && (mappedEntries.length > 0
            ? <KeyValueTable label={t('test.mapped_data')} entries={mappedEntries} />
            : <EmptyNote>{t('test.no_mapping_data')}</EmptyNote>)}
          {activeTab?.id === 'mrz' && <MrzView pages={mrzPages} />}
          {activeTab?.id === 'viz' && <VizView pages={vizPages} />}
          {activeTab?.id === 'norm' && <NormView pages={normPages} />}
          {activeTab?.id === 'debug' && <DebugView entries={debugEntries} />}
          {activeTab?.id === 'face' && (
            <FaceView face={faceB64} signature={signatureB64} faceMatch={faceMatch} faceVector={faceVectorData} />
          )}
          {activeTab?.id === 'json' && (
            <CodeBlock text={stringifyResponse(responseJson)} copyText={JSON.stringify(responseJson, null, 2)} />
          )}
        </>,
      )}
      {createCaseDialog}
    </>,
  );
};
